import React from 'react'
import { connect } from 'react-redux'
import { bindActionCreators, Dispatch, Action } from 'redux'
import { IApplicationState } from '@Site/rootReducer'
import Popup from '@SiteComponents/Popup'
import ExternalTokenForm from '@SiteComponents/ExternalTokenForm'

import toastr from 'toastr'
import 'toastr/build/toastr.css'

import {
  createOrUpdateExternalToken,
  deleteExternalToken,
  fetchExternalTokens,
} from '@SiteModules/ExternalToken/actions'

import {
  IExternalToken,
  ICreateOrUpdateExternalTokenPayload,
  IDeleteExternalTokenPayload,
  IFetchExternalTokensPayload,
} from '@SiteModules/ExternalToken/types'

import './ExternalTokenPopup.scss'

interface ExternalTokenProps {
  readonly loading: boolean
  readonly subtitleId: number | undefined
  readonly externalTokens: IExternalToken[]
  closePopup(): void
  createOrUpdateExternalToken(payload: ICreateOrUpdateExternalTokenPayload): void
  deleteExternalToken(payload: IDeleteExternalTokenPayload): void
  fetchExternalTokens(payload: IFetchExternalTokensPayload): void
}

interface ExternalTokenState {}

class ExternalToken extends React.Component<ExternalTokenProps, ExternalTokenState> {
  componentDidMount() {
    const { subtitleId } = this.props
    if (typeof(subtitleId) !== 'number') { return }

    this.props.fetchExternalTokens({ subtitleId })
  }

  setValidityOfExternalToken = (validityInDays: number) => {
    const { subtitleId } = this.props
    if (typeof(subtitleId) !== 'number') { return }

    this.props.createOrUpdateExternalToken({ subtitleId, validityInDays })
  }

  deleteExternalToken = (externalTokenId: string) => {
    const { subtitleId } = this.props
    if (typeof(subtitleId) !== 'number') { return }

    this.props.deleteExternalToken({ subtitleId, externalTokenId })
  }

  fallbackCopyToClipboard = (externalTokenUrl: string) => {
    const textArea = document.createElement('textarea')
    textArea.value = externalTokenUrl
    textArea.setAttribute('readonly', '')
    textArea.style.position = 'fixed'
    textArea.style.opacity = '0'
    document.body.appendChild(textArea)
    textArea.select()

    try {
      const copied = document.execCommand('copy')
      if (!copied) { throw new Error('Copy command failed') }

      toastr.success('Your link for sharing is copied to the clipboard!')
    } catch (e) {
      toastr.error('Unable to copy the sharing link. Please copy it manually.')
    } finally {
      document.body.removeChild(textArea)
    }
  }

  copyToClipboard = async (externalTokenUrl: string) => {
    if (!navigator.clipboard || !navigator.clipboard.writeText) {
      this.fallbackCopyToClipboard(externalTokenUrl)
      return
    }

    try {
      await navigator.clipboard.writeText(externalTokenUrl)
      toastr.success('Your link for sharing is copied to the clipboard!')
    } catch (e) {
      this.fallbackCopyToClipboard(externalTokenUrl)
    }
  }

  render() {
    const {
      loading,
      closePopup,
      externalTokens,
    } = this.props

    return (
      <Popup
        className="ExternalTokenPopup LanguagesView__element"
        closePopup={closePopup}
        zIndex={200000}
      >
        <ExternalTokenForm
          loading={loading}
          externalTokens={externalTokens}
          copyToClipboard={this.copyToClipboard}
          deleteExternalToken={this.deleteExternalToken}
          setValidityOfExternalToken={this.setValidityOfExternalToken}
          renderedFromDashboard
          deleteButtonTestId="external-token-delete"
        />
      </Popup>
    )
  }
}

function mapStateToProps(state: IApplicationState) {
  const { externalToken, popup } = state

  return {
    loading: externalToken.loading,
    subtitleId: popup.editingSubtitleIds[0],
    externalTokens: externalToken.externalTokens,
  }
}

function mapDispatchToProps(dispatch: Dispatch<Action>) {
  return bindActionCreators({
    createOrUpdateExternalToken,
    deleteExternalToken,
    fetchExternalTokens,
  }, dispatch)
}

export default connect(mapStateToProps, mapDispatchToProps)(ExternalToken)
